import { getInstituteRollNumber, getSystemStudentId } from "./studentDisplayIdentifiers";

const BOM = "\uFEFF";

function escCell(v) {
  const s = String(v ?? "");
  return `"${s.replace(/"/g, '""')}"`;
}

function rowsToCsv(rows) {
  return rows.map((row) => row.map(escCell).join(",")).join("\r\n");
}

function triggerDownload(content, fileName) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

const G = { DEPARTMENT: 0, CLASS: 1, INDIVIDUAL: 2 };

/**
 * CSV export for subject-wise class attendance (from GET /analytics/reports/subject).
 */
export function downloadSubjectReportCsv({ subjectReport, period, subjectName }) {
  if (!subjectReport?.students?.length) return false;
  const safePeriod = period || "monthly";
  const subjLabel = subjectName || subjectReport?.subject?.name || "subject";
  const classLabel = subjectReport?.class?.label || "";

  const studentRows = [
    ["Student ID", "Roll Number", "Name", "Batch", "Total Lectures", "Present", "Late", "Absent", "Attendance %"],
  ];
  subjectReport.students.forEach((s) => {
    studentRows.push([
      s.studentId || getSystemStudentId(s) || "",
      s.rollNumber ?? s.rollNo ?? getInstituteRollNumber(s) ?? "",
      s.name || "",
      s.batch || "",
      s.totalLectures ?? "",
      s.present ?? "",
      s.late ?? "",
      s.absent ?? "",
      s.percentage ?? "",
    ]);
  });

  const lectureRows = [
    ["Lecture Date", "Slot ID", "Batch", "Present", "Late", "Absent", "Total Students", "Attendance %"],
  ];
  (subjectReport.lectures || []).forEach((lec) => {
    lectureRows.push([
      lec.lectureDate || "",
      lec.slotId ?? "",
      lec.batch || "",
      lec.present ?? "",
      lec.late ?? "",
      lec.absent ?? "",
      lec.totalStudents ?? "",
      lec.percentage ?? "",
    ]);
  });

  const parts = [
    `"Subject","${String(subjLabel).replace(/"/g, '""')}"`,
    `"Class","${String(classLabel).replace(/"/g, '""')}"`,
    "",
    "Student-wise",
    rowsToCsv(studentRows),
    "",
    "Lecture-wise",
    rowsToCsv(lectureRows),
  ];
  const slug = String(subjLabel).replace(/[^\w.-]+/g, "_").slice(0, 40);
  triggerDownload(BOM + parts.join("\r\n"), `subject_${slug}_${safePeriod}.csv`);
  return true;
}

/**
 * Client-side CSV export for Advanced Student Reports (matches loaded API data).
 */
export function downloadAdvancedReportCsv({
  granularity,
  data,
  period,
  selectedClass,
  selectedStudent,
  studentsInClass,
}) {
  if (!data && !(granularity === G.CLASS && selectedClass && !selectedStudent && studentsInClass?.length)) {
    return;
  }

  const safePeriod = period || "monthly";

  if (granularity === G.CLASS && selectedClass && !selectedStudent && Array.isArray(studentsInClass) && studentsInClass.length) {
    const rows = [["Name", "Email", "Student ID", "Roll Number", "Status"]];
    studentsInClass.forEach((s) => {
      rows.push([
        s.name || "",
        s.email || "",
        s.studentId || getSystemStudentId(s) || "",
        s.rollNumber ?? s.rollNo ?? getInstituteRollNumber(s) ?? "",
        s.status || "",
      ]);
    });
    triggerDownload(BOM + rowsToCsv(rows), `students_${safePeriod}.csv`);
    return;
  }

  if (granularity === G.DEPARTMENT && data?.departments) {
    const rows = [
      [
        "Department",
        "Code",
        "Students",
        "Present",
        "Absent",
        "Attendance %",
        "Assignment Completion %",
      ],
    ];
    data.departments.forEach((d) => {
      rows.push([
        d.name || "",
        d.code || "",
        d.totalStudents ?? "",
        d.attendance?.present ?? "",
        d.attendance?.absent ?? "",
        d.attendance?.percentage ?? "",
        d.assignments?.completionRate ?? "",
      ]);
    });
    triggerDownload(BOM + rowsToCsv(rows), `department_report_${safePeriod}.csv`);
    return;
  }

  if (granularity === G.CLASS && data?.classes) {
    const rows = [
      [
        "Class",
        "Section",
        "Department",
        "Class Teacher",
        "Students",
        "Present",
        "Absent",
        "Attendance %",
        "Assignment Completion %",
      ],
    ];
    data.classes.forEach((c) => {
      rows.push([
        c.name || "",
        c.section || "",
        c.departmentName || "",
        c.classTeacher || "",
        c.totalStudents ?? "",
        c.attendance?.present ?? "",
        c.attendance?.absent ?? "",
        c.attendance?.percentage ?? "",
        c.assignments?.completionRate ?? "",
      ]);
    });
    triggerDownload(BOM + rowsToCsv(rows), `class_report_${safePeriod}.csv`);
    return;
  }

  if (granularity === G.INDIVIDUAL && data?.student) {
    const st = data.student;
    const summaryRows = [
      ["Field", "Value"],
      ["Name", st.name || ""],
      ["Email", st.email || ""],
      ["Student ID", st.studentId || getSystemStudentId(st) || ""],
      ["Roll Number", st.rollNumber ?? st.rollNo ?? getInstituteRollNumber(st) ?? ""],
      ["Class", st.class || ""],
      ["Section", st.section || ""],
      ["Department", st.department || ""],
      ["Institute", st.institute || ""],
      ["Attendance %", data.attendance?.percentage ?? ""],
      ["Total Sessions", data.attendance?.total ?? ""],
      ["Assignment Completion %", data.assignments?.completionRate ?? ""],
      ["Avg Score %", data.assignments?.avgScore ?? ""],
      ["Materials", data.materials?.total ?? ""],
      ["Exams total", data.exams?.total ?? ""],
      ["Exams marked", data.exams?.marked ?? ""],
      ["Exam avg %", data.exams?.avgPercent ?? ""],
      ["Exam pass rate %", data.exams?.passRate ?? ""],
    ];

    const att = (data.attendance?.details || []).map((d) => [
      d.date ? new Date(d.date).toISOString() : "",
      d.status || "",
      d.subject || "",
      d.method || "",
      d.markedAt ? new Date(d.markedAt).toISOString() : "",
    ]);
    const attHeader = [["Date", "Status", "Subject", "Method", "Marked At"]];

    const asg = (data.assignments?.details || []).map((d) => [
      d.name || "",
      d.subject || "",
      d.status || "",
      d.score != null ? String(d.score) : "",
      d.maxScore != null ? String(d.maxScore) : "",
      d.dueDate ? new Date(d.dueDate).toISOString() : "",
      d.submittedAt ? new Date(d.submittedAt).toISOString() : "",
      d.isLate ? "Yes" : "No",
    ]);
    const asgHeader = [["Assignment", "Subject", "Status", "Score", "Max Score", "Due Date", "Submitted", "Late"]];

    const mat = (data.materials?.details || []).map((d) => [
      d.name || "",
      d.subject || "",
      d.filesCount != null ? String(d.filesCount) : "",
      d.createdAt ? new Date(d.createdAt).toISOString() : "",
    ]);
    const matHeader = [["Material", "Subject", "Files", "Uploaded"]];

    const exm = (data.exams?.details || []).map((d) => [
      d.name || "",
      d.examType || "",
      d.subject || "",
      d.examDate ? new Date(d.examDate).toISOString() : "",
      d.marksObtained != null ? String(d.marksObtained) : "",
      d.totalMarks != null ? String(d.totalMarks) : "",
      d.percent != null ? String(d.percent) : "",
      d.passed === true ? "Pass" : d.passed === false ? "Fail" : "",
      d.status || "",
    ]);
    const exmHeader = [["Exam", "Type", "Subject", "Date", "Marks", "Max", "Percent", "Result", "Status"]];

    const parts = [
      rowsToCsv(summaryRows),
      "",
      "Attendance details",
      rowsToCsv([...attHeader, ...att]),
      "",
      "Assignment details",
      rowsToCsv([...asgHeader, ...asg]),
      "",
      "Study materials",
      rowsToCsv([...matHeader, ...mat]),
      "",
      "Exam details",
      rowsToCsv([...exmHeader, ...exm]),
    ];

    triggerDownload(BOM + parts.join("\r\n"), `student_${st.id}_${safePeriod}.csv`);
  }
}
